import { useMemo } from 'react';
import { Stack, Typography, Divider } from '@mui/material';
import { doorIcon, layersIcon, squareMetersIcon } from '../config/icons';

import { formatPrice } from '../utils';
import { EProjectPolygonPriceBy } from '../types/config';
import { getFloorDisplay } from './inventory/config';

export type ApartmentInfoProps = {
  t: (key: string, options?: any) => string;
  apartmentLanding: any;
};

export function ApartmentInfo({ t, apartmentLanding }: ApartmentInfoProps) {
  const [ifOneOnly, rooms, livingArea, floor, price, priceBy, showPrice] = useMemo(() => {
    const rooms = !!apartmentLanding?.rooms ? apartmentLanding.rooms : false;
    const livingArea = !!apartmentLanding?.livingArea ? apartmentLanding.livingArea : false;
    const floor = getFloorDisplay(apartmentLanding?.minFloor, apartmentLanding?.maxFloor, t);
    const price = apartmentLanding?.price && apartmentLanding?.showPrice ? apartmentLanding.price : false;
    const priceBy = apartmentLanding?.priceBy || EProjectPolygonPriceBy.Total;
    const showPrice = !!price;
    const ifOneOnly = [rooms, livingArea, floor].filter((item) => !!item).length == 1;
    return [ifOneOnly, rooms, livingArea, floor, price, priceBy, showPrice];
  }, [apartmentLanding, t]);

  if (!apartmentLanding) return null;

  return (
    <Stack spacing={2}>
      <Divider style={{ width: '100%' }} />
      {showPrice && (
        <Typography
          fontWeight="600"
          dir="rtl"
          sx={{
            fontSize: '14px',
            color: '#0494FE',
            opacity: 1,
            lineHeight: '24px',
            width: '100%',
            textAlign: 'start',
            display: 'block'
          }}
        >
          {t(
            priceBy === EProjectPolygonPriceBy.Total
              ? 'labels.startingFromMoney'
              : 'labels.startingFromPerSquareMeters',
            { v: formatPrice(Math.abs(Number(price))) },
          )}
        </Typography>
      )}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent={ifOneOnly ? 'flex-start' : 'space-evenly'}
        spacing={1}
        sx={{ width: '100%' }}
      >
        {rooms && (
          <Stack direction="column" alignItems="center" spacing={1}>
            {doorIcon}
            <Typography variant="body1" fontWeight="600" textAlign="center">
              {t('labels.lowerCaseRooms', { v: rooms })}
            </Typography>
          </Stack>
        )}
        {livingArea && (
          <Stack direction="column" alignItems="center" spacing={1}>
            {squareMetersIcon}
            <Typography variant="body1" fontWeight="600" textAlign="center">
              {t('labels.fromM2', { v: livingArea })}
            </Typography>
          </Stack>
        )}
        {floor && (
          <Stack direction="column" alignItems="center" spacing={1}>
            {layersIcon}
            <Stack direction="row" alignItems="center" spacing={.5}>
              <Typography variant="body1" fontWeight="600" textAlign="center">
                {t('labels.floor')}
              </Typography>
              <Typography variant="body1" fontWeight="600" textAlign="center" dir="ltr">
                {floor}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
